import { PlayerManager } from 'chromecast-caf-receiver/cast.framework';
import {
  QueueItem,
  MediaInformation,
  StreamType,
  Image as CastImage,
} from 'chromecast-caf-receiver/cast.framework.messages';
import type { CastSession } from '../types/session';
import { Hermes } from '../network';
import { Logger } from '../logger';

/**
 * Queue manager — fetches the next episode from Hermes and inserts it
 * into the Cast queue for seamless autoplay.
 *
 * Triggered by:
 * - Checkpoint loop when remaining time < 60s (prefetch)
 * - ENDED event as fallback
 *
 * Bypasses LIVE streams (no "next episode" concept).
 */

const LOG_TAG = 'QUEUE';

/** Cloudinary transform URL prefix for thumbnails. */
const IMAGE_BASE_URL =
  'https://images.brasilparalelo.com.br/hvzbb2hdx/image/upload/c_scale,f_auto,w_300/v1/';

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

/** Last mediaId for which we already prefetched — avoids duplicate fetches. */
let lastPrefetchedMediaId: string | null = null;

// ---------------------------------------------------------------------------
// Custom data types for queue items
// ---------------------------------------------------------------------------

interface QueueItemCustomData {
  mediaId: string;
  playlistId: string;
  playlistSlug: string;
  [key: string]: unknown;
}

interface QueueItemMetadata {
  title: string;
  subtitle: string;
  images?: CastImage[];
  customData: QueueItemCustomData;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Helper: extract mediaId/playlistId from the last LOAD request
// ---------------------------------------------------------------------------

interface LoadCustomData {
  mediaId?: string;
  playlistId?: string;
  [key: string]: unknown;
}

interface LoadMetadata {
  customData?: LoadCustomData;
  [key: string]: unknown;
}

function extractMediaIds(
  session: CastSession,
): { mediaId: string; playlistId: string } | null {
  const loadRequest = session.loadRequestData;
  if (!loadRequest) return null;

  const metadata = loadRequest.media.metadata as LoadMetadata | undefined;
  const customData = metadata?.customData;
  if (!customData?.mediaId || !customData.playlistId) return null;

  return {
    mediaId: customData.mediaId,
    playlistId: customData.playlistId,
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Resets the prefetch guard. Call this on every new LOAD so the next
 * episode can be prefetched for the new media.
 */
export function resetQueuePrefetch(): void {
  lastPrefetchedMediaId = null;
}

/**
 * Fetches the next episode via `GetNextMedia` and inserts a queue item.
 *
 * No-op if:
 * - Already prefetched for the current mediaId
 * - Stream is LIVE
 * - No mediaId/playlistId available
 *
 * Safe to call multiple times — the `lastPrefetchedMediaId` guard
 * prevents duplicate Hermes calls.
 */
export function prefetchAndQueueNext(
  playerManager: PlayerManager,
  session: CastSession,
): void {
  // LIVE bypass
  const loadRequest = session.loadRequestData;
  if (loadRequest?.media?.streamType === StreamType.LIVE) {
    return;
  }

  const ids = extractMediaIds(session);
  if (!ids) {
    Logger.warn(LOG_TAG, 'No mediaId/playlistId — skipping queue prefetch');
    return;
  }

  const { mediaId, playlistId } = ids;

  // Already prefetched for this media
  if (lastPrefetchedMediaId === mediaId) {
    return;
  }

  lastPrefetchedMediaId = mediaId;

  Logger.debug(LOG_TAG, 'Prefetching next episode', { mediaId, playlistId });

  Hermes.client
    .GetNextMedia({ mediaId, playlistId })
    .then((result) => {
      const nextMedia = result.media.next_media_object;

      if (!nextMedia) {
        Logger.info(LOG_TAG, 'No next episode in playlist');
        return;
      }

      const streamUrl = nextMedia.manifest.url;
      if (!streamUrl) {
        Logger.warn(LOG_TAG, 'Next media has no manifest URL', {
          nextMediaId: nextMedia.id,
        });
        return;
      }

      const playlist = result.media.playlist;

      // Build QueueItem
      const queueItem = new QueueItem();
      queueItem.autoplay = true;
      queueItem.media = new MediaInformation();
      queueItem.media.contentId = `/playlists/${playlist.slug}/media/${nextMedia.id}`;
      queueItem.media.duration = nextMedia.duration;
      queueItem.media.streamType = StreamType.BUFFERED;
      const thumbnailPath = playlist.thumbnails?.dvd?.image;

      const metadata: QueueItemMetadata = {
        title: nextMedia.name,
        subtitle: playlist.name,
        customData: {
          mediaId: nextMedia.id,
          playlistId: playlist.id,
          playlistSlug: playlist.slug,
        },
      };

      if (thumbnailPath) {
        metadata.images = [
          new CastImage(`${IMAGE_BASE_URL}${thumbnailPath}`),
        ];
      }

      queueItem.media.metadata = metadata;

      // Insert into queue
      const queueManager = playerManager.getQueueManager();
      if (!queueManager) {
        Logger.warn(LOG_TAG, 'QueueManager not available');
        return;
      }

      queueManager.insertItems([queueItem]);

      Logger.info(LOG_TAG, 'Next episode queued', {
        nextMediaId: nextMedia.id,
        title: nextMedia.name,
        duration: nextMedia.duration,
      });
    })
    .catch((err: unknown) => {
      // Reset so it can be retried (e.g. on ENDED fallback)
      lastPrefetchedMediaId = null;
      Logger.error(LOG_TAG, 'Failed to prefetch next episode', err);
    });
}
